/* eslint-disable no-duplicate-imports */
/**
 * AuthProvider - Authentication Context Provider
 *
 * Implements proactive token validation on app startup to prevent
 * unexpected logouts when user has expired token in localStorage.
 *
 * @see docs/TOKEN_VALIDATION.md for complete documentation
 */
import type React from "react";
import { useEffect, useState, type ReactNode } from "react";
import {
	getAccessToken,
	getEmail,
	setAccessToken,
	setEmail,
	clearAuthData,
} from "../utils/storage/authStorage";
import { router } from "../router";
import {
	AuthContext,
	type AuthContextValue,
	type AuthState,
} from "./AuthContext";
import OneSignal from "onesignal-cordova-plugin";
import { Capacitor } from "@capacitor/core";
import { validateToken } from "../api/api";
import * as Sentry from "@sentry/capacitor";

interface AuthProviderProps {
	children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [authState, setAuthState] = useState<AuthState>({
		token: null,
		email: null,
		isAuthenticated: false,
		isLoading: true,
		tokenValidation: "pending",
	});

	// Load auth data on mount
	useEffect(() => {
		const loadAuthData = async (): Promise<void> => {
			try {
				const [token, email] = await Promise.all([
					getAccessToken(),
					getEmail(),
				]);

				// If no token, user is not authenticated
				if (!token) {
					setAuthState({
						token: null,
						email: null,
						isAuthenticated: false,
						isLoading: false,
						tokenValidation: "invalid",
					});
					return;
				}

				// Token exists, validate it
				// console.log('[AuthProvider] Token found, validating...');
				const validationResult = await validateToken();
				console.log("[AuthProvider] Validation result:", validationResult);

				if (validationResult === "valid" || validationResult === "offline") {
					// Token is valid or we're offline (trust the token)
					if (
						email &&
						import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true" &&
						Capacitor.isNativePlatform()
					) {
						OneSignal.login(email);
					}

					// Set Sentry user context for error tracking
					if (email) {
						Sentry.setUser({
							email: email,
						});
					}

					setAuthState({
						token,
						email,
						isAuthenticated: true,
						isLoading: false,
						tokenValidation: validationResult,
					});

					// Update router context
					router.update({
						context: { token },
					});
				} else {
					// Token is invalid, clear auth data silently
					console.log("[AuthProvider] Token invalid, clearing auth data");
					await clearAuthData();

					if (
						import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true" &&
						Capacitor.isNativePlatform()
					) {
						OneSignal.logout();
					}

					// Clear Sentry user context
					Sentry.setUser(null);

					setAuthState({
						token: null,
						email: null,
						isAuthenticated: false,
						isLoading: false,
						tokenValidation: "invalid",
					});

					router.update({
						context: { token: null },
					});
				}
			} catch (error) {
				console.error("Error loading auth data:", error);
				setAuthState({
					token: null,
					email: null,
					isAuthenticated: false,
					isLoading: false,
					tokenValidation: "invalid",
				});
			}
		};

		loadAuthData().catch((error) => {
			console.error("Error loading auth data:", error);
		});
	}, []);

	const login = async (token: string, email: string): Promise<void> => {
		try {
			await Promise.all([setAccessToken(token), setEmail(email)]);
			// AWESOME_OFFLINE
			if (
				import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true" &&
				Capacitor.isNativePlatform()
			) {
				OneSignal.login(email);
			}

			// Set Sentry user context for error tracking
			Sentry.setUser({
				email: email,
			});

			setAuthState({
				token,
				email,
				isAuthenticated: true,
				isLoading: false,
				tokenValidation: "valid",
			});

			router.update({
				context: { token },
			});
		} catch (error) {
			console.error("Error during login:", error);
			throw error;
		}
	};

	const logout = async (): Promise<void> => {
		try {
			await clearAuthData();

			if (
				import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true" &&
				Capacitor.isNativePlatform()
			) {
				OneSignal.logout();
			}

			// Clear Sentry user context
			Sentry.setUser(null);

			setAuthState({
				token: null,
				email: null,
				isAuthenticated: false,
				isLoading: false,
				tokenValidation: "invalid",
			});

			router.update({
				context: { token: null },
			});

			// Navigate to home after logout
			await router.navigate({ to: "/" });
		} catch (error) {
			console.error("Error during logout:", error);
			throw error;
		}
	};

	const refreshAuthState = async (): Promise<void> => {
		try {
			const [token, email] = await Promise.all([getAccessToken(), getEmail()]);

			if (!token) {
				setAuthState((previous) => ({
					...previous,
					token: null,
					email: null,
					isAuthenticated: false,
					tokenValidation: "invalid",
				}));
				return;
			}

			// Validate token when refreshing
			const validationResult = await validateToken();

			if (validationResult === "valid" || validationResult === "offline") {
				// Set Sentry user context
				if (email) {
					Sentry.setUser({
						email: email,
					});
				}

				setAuthState((previous) => ({
					...previous,
					token,
					email,
					isAuthenticated: true,
					tokenValidation: validationResult,
				}));
			} else {
				// Token is invalid, clear it
				await clearAuthData();

				// Clear Sentry user context
				Sentry.setUser(null);

				setAuthState((previous) => ({
					...previous,
					token: null,
					email: null,
					isAuthenticated: false,
					tokenValidation: "invalid",
				}));
			}
		} catch (error) {
			console.error("Error refreshing auth state:", error);
		}
	};

	const contextValue: AuthContextValue = {
		...authState,
		login,
		logout,
		refreshAuthState,
	};

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
};
